import React, { useState } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import HeathrowFlightSelector from "../components/HeathrowFlightSelector";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import "../styles/ArrivalAssistant.css";
import { flightNumKey } from "../utils/heathrowFlightSelection";

const todayUpdatedLabel = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "Europe/London",
}).format(new Date());

const ArrivalAssistant = () => {
  const [flightNumber, setFlightNumber] = useState("KU103");
  const [flightDirection, setFlightDirection] = useState("Arrival");
  const [flightDate, setFlightDate] = useState("");
  const [flightAnchorUtc, setFlightAnchorUtc] = useState("");
  const [nationalityGroup, setNationalityGroup] = useState("NonEEA");
  const [visitPurpose, setVisitPurpose] = useState("student");
  const [ageGroup, setAgeGroup] = useState("adult");
  const [firstTimeUk, setFirstTimeUk] = useState(true);
  const [visaType, setVisaType] = useState("student");
  const [travellingWithChildren, setTravellingWithChildren] = useState(false);
  const [travellingWithElderly, setTravellingWithElderly] = useState(false);
  const [onwardMode, setOnwardMode] = useState("undecided");
  const [passengers, setPassengers] = useState(1);
  const [bags, setBags] = useState(2);
  const [hoveredSection, setHoveredSection] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleSubmit = async e => {
    e.preventDefault();
    const cleanFlight = flightNumKey(flightNumber);
    if (!cleanFlight || !flightDate || !flightAnchorUtc) {
      setError("Choose the exact Heathrow flight date and time before creating your guide.");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const payload = {
        flight_number: cleanFlight,
        direction: flightDirection,
        flight_date: flightDate,
        anchor_utc: flightAnchorUtc,
        nationality_group: nationalityGroup,
        visit_purpose: visitPurpose,
        age_group: ageGroup,
        first_time_uk: firstTimeUk,
        visa_type: visaType,
        travelling_with_children: travellingWithChildren,
        travelling_with_elderly: travellingWithElderly,
        passengers: Number(passengers) || 1,
        bags: Number(bags) || 0,
        onward_travel_mode: onwardMode
      };

      const res = await api.post("/api/arrival-assistant", payload);
      setResult(res.data);
    } catch (err) {
      console.error("❌ Arrival assistant error:", err);
      const msg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        "We could not create your arrival guide. Please check your flight number and try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const renderSection = (title, lines, defaultOpen = false) => {
    if (!lines || !lines.length) return null;
    const cleanLines = lines.filter(Boolean);
    if (!cleanLines.length) return null;

    return (
      <details className="card arrival-section collapsible" key={title} open={defaultOpen}>
        <summary
          className={`section-summary ${hoveredSection === title ? "hovered" : ""}`}
          onMouseEnter={() => setHoveredSection(title)}
          onMouseLeave={() => setHoveredSection(null)}
        >
          <h3 style={{ margin: 0 }}>{title}</h3>
        </summary>
        <ul className="collapsible-body">
          {cleanLines.map((line, idx) => (
            <li key={idx}>{line}</li>
          ))}
        </ul>
      </details>
    );
  };

  const renderSections = () => {
    if (!result || !result.guide || !result.guide.sections) return null;

    const { sections } = result.guide;
    const scenario = result.scenario;

    if (scenario === "DEPARTURE") {
      return (
        <>
          {renderSection("Before you go to Heathrow", sections.departure_overview)}
          {renderSection("At the airport", sections.departure_steps)}
        </>
      );
    }

    // ARRIVAL sections
    return (
      <>
        {renderSection("On the plane", sections.in_plane)}
        {renderSection("Walking to passport control", sections.walking_to_border)}
        {renderSection("Choosing the right queue", sections.choosing_queue)}
        {renderSection("Typical questions at immigration", sections.immigration_questions)}
        {renderSection(
          "Documents to keep in your hand luggage",
          sections.documents_to_have_ready
        )}
        {renderSection("Baggage reclaim", sections.baggage_area)}
        {renderSection("Customs", sections.customs)}
        {renderSection(
          "Meeting family, friends or your driver",
          sections.meeting_family_or_driver
        )}
        {renderSection("Onward travel from Heathrow", sections.onward_travel)}
        {renderSection("Your first day in the UK", sections.day_one)}
        {renderSection("Your first week in the UK", sections.first_week)}
      </>
    );
  };

  const renderSummaryHeader = () => {
    if (!result) return null;

    const { scenario, flight, timeline, queue_estimate } = result;
    const weather = result?.guide?.weather;

    return (
      <section className="card arrival-summary">
        <h2>Your personalised Heathrow guide</h2>

        <p className="lede">{result.guide?.summary}</p>

        {result?.guide?.risk_profile && (
          <p className="muted">
            Support level:{" "}
            <strong>
              {result.guide.risk_profile.band.replace("_", " ")}
            </strong>{" "}
            (score {result.guide.risk_profile.score})
          </p>
        )}

        {result?.guide?.risk_profile && (
          <p className="muted">
            Examples: an 18-year-old first-time Non-EEA student will see higher support,
            while a 45-year-old UK business traveller sees a shorter, simpler script.
          </p>
        )}

        <div className="summary-grid">
          <div>
            <h4>Flight details</h4>
            <p>
              <strong>
                {flight?.iata_flight_identifier || flight?.flight_number}
              </strong>{" "}
              {flight?.airline_name && <>· {flight.airline_name}</>}
            </p>
            <p>
              {scenario === "ARRIVAL" ? "Arriving into" : "Departing from"} Terminal{" "}
              {flight?.terminal || "TBC"}
            </p>
            <p>
              Scheduled local time:{" "}
              {timeline?.scheduled_time_display || flight?.scheduled_local}
            </p>
            {flight?.origin_airport_name && scenario === "ARRIVAL" && (
              <p>
                From: {flight.origin_airport_name}
                {flight.origin_city ? ` (${flight.origin_city})` : ""}
              </p>
            )}
            {flight?.destination_airport_name && scenario === "DEPARTURE" && (
              <p>
                To: {flight.destination_airport_name}
                {flight.destination_city ? ` (${flight.destination_city})` : ""}
              </p>
            )}
          </div>

          <div>
            <h4>Time inside the terminal</h4>
            {scenario === "ARRIVAL" && timeline && (
              <p>
                From landing to leaving the arrivals hall, allow{" "}
                <strong>
                  {timeline.suggested_min_total_minutes}–
                  {timeline.suggested_max_total_minutes} minutes
                </strong>{" "}
                for immigration, baggage and walking.
              </p>
            )}
            {!timeline && <p>We could not build a time estimate for this flight.</p>}

            {queue_estimate && (
              <>
                <h4 style={{ marginTop: "0.75rem" }}>
                  {scenario === "ARRIVAL" ? "Immigration queues" : "Security queues"}
                </h4>
                <p>
                  Latest snapshot for Terminal {queue_estimate.terminal}:{" "}
                  {queue_estimate.wait_message || "typical queues"}.
                </p>
                {queue_estimate.derived_range && (
                  <p>Range: {queue_estimate.derived_range}</p>
                )}
                <p className="muted">
                  Snapshot time: {queue_estimate.snapshot_time_display}
                </p>
              </>
            )}
          </div>

          {weather && (
            <div className="weather-card">
              <h4>Weather around arrival</h4>
              <p>
                Expected: <strong>{Math.round(weather.tempC)}°C</strong>
                {weather.precipProb != null && (
                  <> · Rain chance: {Math.round(weather.precipProb)}%</>
                )}
                {weather.windKph != null && (
                  <> · Wind: {Math.round(weather.windKph)} km/h</>
                )}
              </p>
              {weather.summary && <p className="muted">{weather.summary}</p>}
              {weather.items?.length > 0 && (
                <ul className="weather-items">
                  {weather.items.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              )}
              {weather.notes?.length > 0 && (
                <ul className="weather-notes muted">
                  {weather.notes.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <p className="disclaimer">{result.disclaimer}</p>
      </section>
    );
  };

  return (
    <>
      {/* ✅ SEO / Helmet */}
      <Helmet>
        <title>
          Heathrow Arrival Assistant | Immigration Questions, Queues & Step-by-Step Guide
        </title>

        <meta
          name="description"
          content="First time at Heathrow? Get a personalised arrival guide: immigration questions, expected queues (EEA/Non-EEA), baggage reclaim tips, customs channels, and onward travel help."
        />

        <meta
          name="keywords"
          content="first time Heathrow airport, Heathrow immigration questions, Heathrow arrival guide for students, what to expect at Heathrow immigration, UK immigration questions for students, Heathrow customs green red channel, first time UK visitor what to bring, student visa UK documents needed, Heathrow Terminal 4 immigration queue time, Heathrow arrival process Non-EEA"
        />

        <link
          rel="canonical"
          href="https://www.cheapcabgrab.com/arrival-assistant"
        />

        <meta
          property="og:title"
          content="Heathrow Arrival Assistant | Immigration & Arrival Guide"
        />
        <meta
          property="og:description"
          content="Personalised Heathrow arrival guide with immigration questions, queue times, baggage reclaim timing, customs channels and onward travel tips."
        />
        <meta
          property="og:url"
          content="https://www.cheapcabgrab.com/arrival-assistant"
        />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Heathrow Arrival Assistant",
            url: "https://www.cheapcabgrab.com/arrival-assistant",
            description:
              "Personalised Heathrow arrival guide using live-based queue snapshots and flight data. Immigration questions, queue times, baggage reclaim, customs and onward travel tips.",
            about: [
              {
                "@type": "Service",
                name: "Heathrow Taxi",
                url: "https://www.cheapcabgrab.com/heathrow-taxi"
              },
              {
                "@type": "Service",
                name: "Airport Transfers",
                url: "https://www.cheapcabgrab.com"
              }
            ]
          ,
          mainEntity: {
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What questions do they ask at UK immigration for students?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Expect questions about your course, university, accommodation, finances, and return plans. We tailor prompts so you can answer confidently at Heathrow."
                }
              },
              {
                "@type": "Question",
                name: "How long are Heathrow immigration queues (EEA vs Non-EEA)?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We show recent Heathrow queue snapshots per terminal for EEA and Non-EEA to set expectations and help you plan your arrival buffer."
                }
              },
              {
                "@type": "Question",
                name: "Which customs channel should I use at Heathrow?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Green for nothing to declare, red if you have goods to declare, and blue for EU arrivals carrying EU-dutied goods. We remind you of the right channel for your case."
                }
              },
              {
                "@type": "Question",
                name: "What documents should a first-time UK visitor or student carry?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Passport, visa/BRP or approval, CAS/offer letter, proof of funds, accommodation details, return/onward travel, and any health or insurance documents relevant to your stay."
                }
              }
            ]
          }
          })}
        </script>
      </Helmet>

      <LiveStrip variant="heathrow" />

      <div className="arrival-assistant-page">

        <header className="page-header">
          <h1>Heathrow Arrival Assistant</h1>
          <p>
            A simple, data-based guide from the plane door to meeting your driver or
            family at Heathrow. Based on your flight, passport type and reason for
            visit.
          </p>
        </header>

        {/* Form card */}
        <section className="card arrival-form-card">
          <h2>Tell us about your trip</h2>
          <p>We use these details to shape your guide. Short, simple and personalised.</p>

          <form onSubmit={handleSubmit} className="form-grid">
            <HeathrowFlightSelector
              flightNumber={flightNumber}
              setFlightNumber={setFlightNumber}
              flightDirection={flightDirection}
              setFlightDirection={setFlightDirection}
              flightDate={flightDate}
              setFlightDate={setFlightDate}
              flightAnchorUtc={flightAnchorUtc}
              setFlightAnchorUtc={setFlightAnchorUtc}
              note="Choose the exact Heathrow arrival or departure you want guidance for."
            />

            <div className="form-group">
              <label>Passport / nationality group</label>
              <select
                value={nationalityGroup}
                onChange={e => setNationalityGroup(e.target.value)}
              >
		<option value="UK">UK passport / returning resident</option>
  		<option value="EEA">EU / EEA / Swiss passport</option>
  		<option value="NonEEA">Non-EEA passport</option>
              </select>
            </div>

            <div className="form-group">
              <label>Reason for visit</label>
              <select
                value={visitPurpose}
                onChange={e => setVisitPurpose(e.target.value)}
              >
                <option value="tourist">Tourist / Holiday</option>
                <option value="student">Student</option>
                <option value="business">Business</option>
                <option value="family_visit">Family visit</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label>Age group (main traveller)</label>
              <select value={ageGroup} onChange={e => setAgeGroup(e.target.value)}>
                <option value="child">Child / Teen</option>
                <option value="adult">Adult</option>
                <option value="senior">Senior</option>
              </select>
            </div>

            <div className="form-group">
              <label>Is this your first visit to the UK?</label>
              <div className="inline-options">
                <button
                  type="button"
                  className={firstTimeUk ? "btn btn-primary" : "btn btn-outline"}
                  onClick={() => setFirstTimeUk(true)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={!firstTimeUk ? "btn btn-primary" : "btn btn-outline"}
                  onClick={() => setFirstTimeUk(false)}
                >
                  No
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Visa type (if any)</label>
              <select value={visaType} onChange={e => setVisaType(e.target.value)}>
                <option value="none">No visa / visa-free</option>
                <option value="tourist">Visitor / tourist visa</option>
                <option value="student">Student visa</option>
                <option value="work">Work visa</option>
                <option value="family">Family visa</option>
                <option value="other">Other type</option>
              </select>
            </div>

            <div className="form-group">
              <label>Travelling with children?</label>
              <div className="inline-options">
                <button
                  type="button"
                  className={
                    travellingWithChildren ? "btn btn-primary" : "btn btn-outline"
                  }
                  onClick={() => setTravellingWithChildren(true)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={
                    !travellingWithChildren ? "btn btn-primary" : "btn btn-outline"
                  }
                  onClick={() => setTravellingWithChildren(false)}
                >
                  No
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Travelling with elderly family?</label>
              <div className="inline-options">
                <button
                  type="button"
                  className={
                    travellingWithElderly ? "btn btn-primary" : "btn btn-outline"
                  }
                  onClick={() => setTravellingWithElderly(true)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={
                    !travellingWithElderly ? "btn btn-primary" : "btn btn-outline"
                  }
                  onClick={() => setTravellingWithElderly(false)}
                >
                  No
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>How do you plan to travel from Heathrow?</label>
              <select
                value={onwardMode}
                onChange={e => setOnwardMode(e.target.value)}
              >
                <option value="undecided">I’m not sure yet</option>
                <option value="prebooked_taxi">Pre-booked taxi / minicab</option>
                <option value="uber_app">Uber / app-based taxi on arrival</option>
                <option value="train">Train / Underground / Elizabeth line</option>
                <option value="family_pickup">Family or friends collecting by car</option>
              </select>
            </div>

            <div className="form-group small">
              <label>Number of passengers</label>
              <input
                type="number"
                min="1"
                value={passengers}
                onChange={e => setPassengers(e.target.value)}
              />
            </div>

            <div className="form-group small">
              <label>Checked bags</label>
              <input
                type="number"
                min="0"
                value={bags}
                onChange={e => setBags(e.target.value)}
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Creating your arrival guide…" : "Get my arrival guide"}
              </button>
            </div>
          </form>

          {error && <p className="error">{error}</p>}
        </section>

        {/* Result summary + sections */}
        {renderSummaryHeader()}
        {renderSections()}

        {/* 🔗 Plan your journey block (your snippet embedded) */}
        <section className="card arrival-section">
          <div style={{ marginTop: 0 }}>
            <strong>Plan your journey:</strong>
            <ul style={{ marginTop: 8 }}>
              <li>
                <a href="/heathrow-taxi">Heathrow Taxi Transfers</a>
              </li>
              <li>
                <a href="/train-vs-taxi">Taxi vs Train Comparison</a>
              </li>
              <li>
                <a href="/heathrow-parking-vs-taxi">Parking vs Taxi Comparison</a>
              </li>
              <li>
                <a href="/terminalFinder">Find Heathrow Terminals</a>
              </li>
              <li>
                <a href="/airlineContacts">Airline Contact Numbers</a>
              </li>
              <li>
                <a href="/travelnews">UK Travel News</a>
              </li>
            </ul>
          </div>
        </section>

        <ReviewsBar />


      </div>
    </>
  );
};

export default ArrivalAssistant;
